import { getAuthToken } from './auth';

const API_BASE =
  process.env.REACT_APP_API_ENDPOINT ||
  'https://portal.bcore.com/api/v1';

/**
 * Authenticated API call helper.
 * Attaches the Cognito ID token as the Authorization header.
 */
export async function apiCall(method, path, body = null) {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Not authenticated. Please log in.');
  }

  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
  };

  if (body && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(body);
  }

  const url = `${API_BASE}${path}`;
  const response = await fetch(url, options);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || `API error: ${response.status}`);
  }

  return data;
}

/** GET /contracts - Fetch all contracts (Overall period records). */
export function getContracts() {
  return apiCall('GET', '/contracts');
}

/** GET /contracts/{id} - Fetch all periods for a specific contract. */
export function getContract(contractNumber) {
  return apiCall('GET', `/contracts/${encodeURIComponent(contractNumber)}`);
}

/** POST /contracts - Create a new contract period. */
export function createContract(data) {
  return apiCall('POST', '/contracts', data);
}

/** PUT /contracts/{id} - Update an existing contract period (admin only). */
export function updateContract(contractNumber, data) {
  return apiCall('PUT', `/contracts/${encodeURIComponent(contractNumber)}`, data);
}

/** GET /contracts/{id}/history - Fetch audit history for a contract. */
export function getContractHistory(contractNumber) {
  return apiCall('GET', `/contracts/${encodeURIComponent(contractNumber)}/history`);
}

// --- Document Upload ---

/** POST /documents/upload-url - Get presigned S3 upload URL. */
export function getDocumentUploadUrl(fileName, fileType) {
  return apiCall('POST', '/documents/upload-url', { fileName, fileType });
}

/** Upload file directly to S3 using presigned URL. */
export async function uploadDocumentToS3(uploadUrl, file) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  return response.ok;
}

/** POST /documents/process - Trigger AI extraction on an uploaded document. */
export function processDocument(workspaceId) {
  return apiCall('POST', '/documents/process', { workspaceId });
}

// --- Workspace ---

/** GET /workspace - Fetch workspace items, optionally filtered by status. */
export function getWorkspaceItems(status = null) {
  const params = status ? `?status=${status}` : '';
  return apiCall('GET', `/workspace${params}`);
}

/** PUT /workspace/{id} - Submit user-completed data for a workspace item. */
export function updateWorkspaceItem(workspaceId, userCompletedData) {
  return apiCall('PUT', `/workspace/${encodeURIComponent(workspaceId)}`, { userCompletedData });
}

/** POST /workspace/{id}/approve - Approve a workspace item. */
export function approveWorkspaceItem(workspaceId, reviewNotes) {
  return apiCall('POST', `/workspace/${encodeURIComponent(workspaceId)}/approve`, { action: 'approve', reviewNotes });
}

/** POST /workspace/{id}/approve - Reject a workspace item. */
export function rejectWorkspaceItem(workspaceId, reviewNotes) {
  return apiCall('POST', `/workspace/${encodeURIComponent(workspaceId)}/approve`, { action: 'reject', reviewNotes });
}

/** DELETE /workspace/{id} - Delete a workspace item and its S3 document. */
export function deleteWorkspaceItem(workspaceId) {
  return apiCall('DELETE', `/workspace/${encodeURIComponent(workspaceId)}`);
}

/** GET /documents/{key} - Get presigned download URL for a workspace document. */
export function getDocumentDownloadUrl(documentKey) {
  return apiCall('GET', `/documents/${documentKey}`);
}

// --- Admin ---

/** POST /admin/disable-user - Disable a Cognito user and revoke all sessions (admin only). */
export function disableUser(username, reason) {
  return apiCall('POST', '/admin/disable-user', { username, reason });
}

/** GET /admin/users - List all Cognito users (admin only). */
export function listUsers() {
  return apiCall('GET', '/admin/users');
}

/** POST /admin/enable-user - Re-enable a disabled Cognito user (admin only). */
export function enableUser(username) {
  return apiCall('POST', '/admin/enable-user', { username });
}

/** POST /admin/update-user-groups - Add/remove Cognito groups for a user (admin only). */
export function updateUserGroups(username, groupsToAdd, groupsToRemove) {
  return apiCall('POST', '/admin/update-user-groups', { username, groupsToAdd, groupsToRemove });
}

// --- Financials ---

/** GET /financials - Fetch financial dashboard data for a fiscal year and optional division. */
export function getFinancials(fiscalYear, division = null) {
  const params = [`fiscal_year=${fiscalYear}`];
  if (division && division !== 'All') {
    params.push(`division=${encodeURIComponent(division)}`);
  }
  return apiCall('GET', `/financials?${params.join('&')}`);
}

/** GET /financials/aop - Fetch AOP targets for a fiscal year and optional division. */
export function getAopTargets(fiscalYear, division = null) {
  const params = [`fiscal_year=${fiscalYear}`];
  if (division && division !== 'All') {
    params.push(`division=${encodeURIComponent(division)}`);
  }
  return apiCall('GET', `/financials/aop?${params.join('&')}`);
}

/** GET /growth/dashboard - Fetch growth dashboard data. */
export function getGrowthDashboard() {
  return apiCall('GET', '/growth/dashboard');
}

// --- Finance Import ---

/** POST /financials/upload-url - Get presigned S3 URL for financial spreadsheet upload. */
export function getFinanceUploadUrl(fileName, fileType) {
  return apiCall('POST', '/financials/upload-url', { fileName, fileType });
}

/** POST /financials/upload - Process uploaded financial spreadsheet from S3. */
export function processFinanceUpload({ s3Key, fiscalYear, sheet, closeMonth }) {
  const body = { s3_key: s3Key, fiscal_year: fiscalYear, sheet };
  if (closeMonth) body.close_month = closeMonth;
  return apiCall('POST', '/financials/upload', body);
}

// --- Delivery / UTE Import ---

/** POST /delivery/upload-url — Get presigned S3 URL for UTE time data upload. */
export function getUteUploadUrl(fileName, fileType) {
  return apiCall('POST', '/delivery/upload-url', { fileName, fileType });
}

/** POST /delivery/upload — Process uploaded UTE time data from S3. */
export function processUteUpload({ s3Key, year }) {
  return apiCall('POST', '/delivery/upload', { s3_key: s3Key, year });
}

/** GET /delivery/data — Fetch utilization data for the delivery dashboard. */
export function getUtilizationData(year, periodStart, periodEnd, division) {
  let url = `/delivery/data?year=${year}`;
  if (periodStart) url += `&period_start=${periodStart}`;
  if (periodEnd) url += `&period_end=${periodEnd}`;
  if (division && division !== 'All') url += `&division=${encodeURIComponent(division)}`;
  return apiCall('GET', url);
}

// --- Growth Import ---

/** POST /growth/upload-url - Get presigned S3 URL for Pipedrive export upload. */
export function getGrowthUploadUrl(fileName, fileType) {
  return apiCall('POST', '/growth/upload-url', { fileName, fileType });
}

/** POST /growth/upload - Process uploaded Pipedrive export and update growth dashboard. */
export function processGrowthUpload({ s3Key, fiscalYear }) {
  return apiCall('POST', '/growth/upload', { s3_key: s3Key, fiscal_year: fiscalYear });
}

/** POST /growth/sync - Sync growth data directly from Pipedrive API. */
export function syncGrowthFromPipedrive(fiscalYear) {
  return apiCall('POST', '/growth/sync', { fiscal_year: fiscalYear });
}

// --- Clearances ---

/** GET /clearances - Fetch all clearance tracking records. */
export function getClearances(status = null) {
  const params = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiCall('GET', `/clearances${params}`);
}

/** POST /clearances - Create a new clearance tracking record. */
export function createClearance(data) {
  return apiCall('POST', '/clearances', data);
}

/** PUT /clearances/{id} - Update an existing clearance tracking record. */
export function updateClearance(clearanceId, data) {
  return apiCall('PUT', `/clearances/${encodeURIComponent(clearanceId)}`, data);
}

/** DELETE /clearances/{id} - Delete a clearance tracking record. */
export function deleteClearance(clearanceId) {
  return apiCall('DELETE', `/clearances/${encodeURIComponent(clearanceId)}`);
}

// --- Visit Requests ---

/** GET /visits - Fetch all visit request records. */
export function getVisitRequests(status = null) {
  const params = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiCall('GET', `/visits${params}`);
}

/** POST /visits - Create a new visit request record. */
export function createVisitRequest(data) {
  return apiCall('POST', '/visits', data);
}

/** PUT /visits/{id} - Update an existing visit request record. */
export function updateVisitRequest(visitId, data) {
  return apiCall('PUT', `/visits/${encodeURIComponent(visitId)}`, data);
}

/** DELETE /visits/{id} - Delete a visit request record. */
export function deleteVisitRequest(visitId) {
  return apiCall('DELETE', `/visits/${encodeURIComponent(visitId)}`);
}

// --- Upload Job Status Polling ---

/** Poll upload job status from S3 via the documents presigned URL endpoint. */
export async function getUploadJobStatus(prefix, jobId) {
  const { downloadUrl } = await apiCall(
    'GET',
    `/documents/${encodeURIComponent(prefix)}/${encodeURIComponent(jobId)}/status.json`
  );
  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error(`Status fetch failed: ${response.status}`);
  }
  return response.json();
}

// --- Agreements (NDA/TA) ---

/** GET /agreements — Fetch agreement records. Currently returns mock data. */
export async function getAgreements(status = null) {
  // TODO: Replace with real API call when backend endpoint is ready
  // return apiCall('GET', `/agreements${status ? '?status=' + encodeURIComponent(status) : ''}`);
  const { MOCK_AGREEMENTS } = await import('../data/mockAgreements');
  if (status) {
    return MOCK_AGREEMENTS.filter((a) => a.status === status);
  }
  return MOCK_AGREEMENTS;
}

/** GET /agreements/{id} — Fetch single agreement. Currently returns mock data. */
export async function getAgreement(id) {
  // TODO: Replace with real API call when backend endpoint is ready
  // return apiCall('GET', `/agreements/${encodeURIComponent(id)}`);
  const { MOCK_AGREEMENTS } = await import('../data/mockAgreements');
  const found = MOCK_AGREEMENTS.find((a) => a.id === id);
  if (!found) throw new Error('Agreement not found');
  return found;
}

/** PUT /agreements/{id} — Update agreement status. Currently a no-op stub. */
export async function updateAgreementStatus(id, status, notes = '') {
  // TODO: Replace with real API call when backend endpoint is ready
  // return apiCall('PUT', `/agreements/${encodeURIComponent(id)}`, { status, notes });
  return { success: true, id, status, notes };
}
